/**
 * チェックリスト雛形・月次チェックリスト・月次タスクの型と入力検証（02 5.3.14 checklist）。
 * - 雛形のタスク項目は 1〜40 件（1 回の TransactWriteItems に収めるため：D-054。超えたら FE-MSG-V-014）
 * - 雛形項目のコード（UUID v7）は並べ替えても変わらない。登録ではサーバーが払い出し、更新では既存の項目に付けて送る（無ければ新しい項目）
 * - 期限ルールは D-055 の書式（DUE_DATE_RULE_PATTERN）。null は対象月の末日
 * - 月次チェックリストの生成は冪等（主キー＝契約 × 年月が既にあれば作らない：要件書 F-16）
 * - 274（旧PCRM に合わせる）：種別は 仕入先（supplier）・販売先（sales）。1 つの月次チェックリストに【仕入先】【販売先】のタスクが入る
 *   （旧の「統合」）。タスクは種別（checklist_template_type）とカテゴリ（task_category：自由入力の文字）を生成のときに雛形から写して持つ
 */
import { z } from 'zod';
import { DUE_DATE_RULE_PATTERN } from '../date/jst';
import { CHECKLIST_TEMPLATE_TYPE_VALUES } from '../enums/checklist-template-type';
import { CONTRACT_STATUS_VALUES } from '../enums/contract-status';
import { MONTHLY_CHECKLIST_STATUS_VALUES } from '../enums/monthly-checklist-status';
import { TASK_CATEGORY_MAX_LENGTH } from '../enums/task-category';
import { TASK_STATUS_VALUES } from '../enums/task-status';
import { zCode, zEnum, zRequiredText, zShortText, zSystemCode, zVersion, zYearMonth } from './common.schema';
import { zNullable } from './customer.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** 雛形のタスク項目の上限（D-054） */
export const MAX_CHECKLIST_TEMPLATE_ITEMS = 40;

export const zDueDateRule = z.string({ message: 'MSG-V-020' }).regex(DUE_DATE_RULE_PATTERN, 'MSG-V-020');

const TEMPLATE_ITEM_SHAPE = {
  display_order: z.number({ message: 'MSG-V-020' }).int('MSG-V-020').min(1, 'MSG-V-020').max(999, 'MSG-V-020'),
  task_name: zRequiredText(200, 'MSG-V-023'),
  /** カテゴリ（自由入力。空は null） */
  task_category: zShortText(TASK_CATEGORY_MAX_LENGTH).optional(),
  due_date_rule: zNullable(zDueDateRule).optional(),
};

const zTemplateItems = <T extends z.ZodTypeAny>(item: T) =>
  z.array(item, { message: 'MSG-V-020' }).min(1, 'MSG-V-020').max(MAX_CHECKLIST_TEMPLATE_ITEMS, 'FE-MSG-V-014');

/** API-CHKL-02 雛形の登録（項目のコードはサーバーが払い出す） */
export const ChecklistTemplateCreateSchema = z
  .object({
    checklist_template_name: zRequiredText(200),
    checklist_template_type: zEnum(CHECKLIST_TEMPLATE_TYPE_VALUES),
    items: zTemplateItems(z.object(TEMPLATE_ITEM_SHAPE).strict()),
  })
  .strict();

/** API-CHKL-04 雛形の更新（items は丸ごと。既存の項目には checklist_template_item_code を付ける） */
export const ChecklistTemplateUpdateSchema = z
  .object({
    checklist_template_name: zRequiredText(200),
    checklist_template_type: zEnum(CHECKLIST_TEMPLATE_TYPE_VALUES),
    items: zTemplateItems(
      z.object({ checklist_template_item_code: zSystemCode.optional(), ...TEMPLATE_ITEM_SHAPE }).strict(),
    ).refine((items) => {
      const codes = items.map((item) => item.checklist_template_item_code).filter(Boolean);
      return new Set(codes).size === codes.length;
    }, 'MSG-V-020'),
    version: zVersion,
  })
  .strict();

export const ChecklistTemplateItemSchema = z.object({
  checklist_template_item_code: z.string(),
  display_order: z.number().int(),
  task_name: z.string(),
  task_category: z.string().nullable(),
  due_date_rule: z.string().nullable(),
});

export const ChecklistTemplateSchema = z
  .object({
    checklist_template_code: z.string(),
    checklist_template_name: z.string(),
    checklist_template_type: z.enum(CHECKLIST_TEMPLATE_TYPE_VALUES),
    item_count: z.number().int(),
    /** 種別ごとの既定の雛形（月次チェックリストの生成で使うもの：種別ごとに登録の最も古い未削除の雛形） */
    is_default: z.boolean(),
    /** 詳細（API-CHKL-02〜05 の応答）だけ。一覧では null */
    items: z.array(ChecklistTemplateItemSchema).nullable(),
  })
  .merge(CommonAttributesSchema);

export const ChecklistTemplateDefaultsResponseSchema = z.object({
  created: z.number().int(),
  checklist_templates: z.array(ChecklistTemplateSchema),
});

export const MonthlyChecklistTaskSchema = z.object({
  monthly_checklist_task_code: z.string(),
  contract_code: z.string(),
  year_month: z.string(),
  task_number: z.number().int(),
  checklist_template_code: z.string(),
  checklist_template_item_code: z.string(),
  task_name: z.string(),
  /** タスクの種別（【仕入先】【販売先】の区切り。生成のときに雛形の種別を写す） */
  checklist_template_type: z.enum(CHECKLIST_TEMPLATE_TYPE_VALUES),
  task_category: z.string().nullable(),
  due_date: z.string(),
  task_status: z.enum(TASK_STATUS_VALUES),
  completed_by: z.string().nullable(),
  completed_by_name: z.string().nullable(),
  completed_at: z.string().nullable(),
  /** 未完了で due_date が JST の今日より前 */
  is_overdue: z.boolean(),
  version: z.number().int(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const MonthlyChecklistSchema = z
  .object({
    contract_code: z.string(),
    year_month: z.string(),
    checklist_template_code: z.string(),
    checklist_template_name: z.string().nullable(),
    checklist_template_type: z.enum(CHECKLIST_TEMPLATE_TYPE_VALUES).nullable(),
    /** タスクに含まれる種別（仕入先 → 販売先の順。両方なら旧の「統合」） */
    checklist_template_types: z.array(z.enum(CHECKLIST_TEMPLATE_TYPE_VALUES)),
    monthly_checklist_status: z.enum(MONTHLY_CHECKLIST_STATUS_VALUES),
    progress_rate: z.number().int(),
    personnel_code: z.string().nullable(),
    personnel_name: z.string().nullable(),
    contract_status: z.enum(CONTRACT_STATUS_VALUES).nullable(),
    task_count: z.number().int(),
    completed_task_count: z.number().int(),
    overdue_task_count: z.number().int(),
    /** 詳細（API-CHKL-07）だけ。一覧では null */
    tasks: z.array(MonthlyChecklistTaskSchema).nullable(),
  })
  .merge(CommonAttributesSchema);

/**
 * API-CHKL-08 タスクの完了・未完了への戻し。
 * completed_by（274：旧の「担当者を選択」）：完了にするときの完了者（有効な利用者の user_code）。省略なら操作者。未完了に戻すときは送らない
 */
export const MonthlyChecklistTaskUpdateSchema = z
  .object({ task_status: zEnum(TASK_STATUS_VALUES), completed_by: zSystemCode.optional(), version: zVersion })
  .strict()
  .refine((value) => value.task_status === 'completed' || value.completed_by === undefined, {
    message: 'MSG-V-020',
    path: ['completed_by'],
  });

export const MonthlyChecklistTaskUpdateResponseSchema = z.object({
  task: MonthlyChecklistTaskSchema,
  monthly_checklist: MonthlyChecklistSchema,
});

/** 期限アラートの日数（0〜31。既定 3：D-057） */
export const DUE_ALERT_DEFAULT_DAYS = 3;

/** API-CHKL-09（02 2.3.2） */
export const DueAlertQuerySchema = z.object({
  within_days: z.coerce
    .number({ message: 'MSG-V-020' })
    .int('MSG-V-020')
    .min(0, 'MSG-V-020')
    .max(31, 'MSG-V-020')
    .default(DUE_ALERT_DEFAULT_DAYS),
  year_month: zYearMonth.optional(),
});

export const DueAlertTaskSchema = MonthlyChecklistTaskSchema.extend({
  personnel_code: z.string().nullable(),
  personnel_name: z.string().nullable(),
  /** 期限までの日数（負は超過日数） */
  days_until_due: z.number().int(),
});

/** 1 回に生成を頼める契約の数（02 5.3.14） */
export const MAX_GENERATION_CONTRACTS = 300;

/** API-CHKL-10 月次チェックリストの生成 */
export const ChecklistGenerationSchema = z
  .object({
    year_month: zYearMonth,
    contract_codes: z
      .array(zCode)
      .min(1, 'MSG-V-020')
      .max(MAX_GENERATION_CONTRACTS, 'MSG-V-020')
      .refine((codes) => new Set(codes).size === codes.length, 'MSG-V-020')
      .optional(),
    checklist_template_code: zSystemCode.optional(),
  })
  .strict();

export const GENERATION_RESULT_VALUES = ['created', 'alreadyExists', 'noTemplate', 'contractNotActive'] as const;
export type GenerationResultType = (typeof GENERATION_RESULT_VALUES)[number];

export const ChecklistGenerationResponseSchema = z.object({
  year_month: z.string(),
  created_count: z.number().int(),
  skipped_count: z.number().int(),
  results: z.array(z.object({ contract_code: z.string(), result: z.enum(GENERATION_RESULT_VALUES) })),
});

/** API-CHKL-11・12 のクエリ */
export const YearMonthQuerySchema = z.object({ year_month: zYearMonth });

/** 243 IT-398：定義外のクエリ項目は 400（strict。02 2.10 D-019 を本文と同じくクエリにも当てる） */
export const ChecklistMatrixQuerySchema = z
  .object({
    year_month: zYearMonth,
    checklist_template_code: zSystemCode.optional(),
  })
  .strict();

export const ChecklistSummarySchema = z.object({
  year_month: z.string(),
  checklist_count: z.number().int(),
  status_counts: z.object({
    notStarted: z.number().int(),
    inProgress: z.number().int(),
    completed: z.number().int(),
  }),
  average_progress_rate: z.string(),
  task_count: z.number().int(),
  completed_task_count: z.number().int(),
  overdue_task_count: z.number().int(),
});

export const ChecklistMatrixCellSchema = z.object({
  monthly_checklist_task_code: z.string(),
  task_status: z.enum(TASK_STATUS_VALUES),
  /** 完了者の氏名（274：旧のセルは完了した担当者名） */
  completed_by_name: z.string().nullable(),
  due_date: z.string(),
  is_overdue: z.boolean(),
  version: z.number().int(),
});

export const ChecklistMatrixSchema = z.object({
  year_month: z.string(),
  columns: z.array(
    z.object({
      checklist_template_item_code: z.string(),
      task_name: z.string(),
      display_order: z.number().int(),
      /** 274：タスクの種別（【仕入先】【販売先】の区切り）・カテゴリ（カテゴリ行） */
      checklist_template_type: z.enum(CHECKLIST_TEMPLATE_TYPE_VALUES),
      task_category: z.string().nullable(),
    }),
  ),
  rows: z.array(
    z.object({
      contract_code: z.string(),
      personnel_code: z.string().nullable(),
      personnel_name: z.string().nullable(),
      checklist_template_code: z.string(),
      progress_rate: z.number().int(),
      cells: z.record(ChecklistMatrixCellSchema),
    }),
  ),
});

export const CHECKLIST_TEMPLATE_LIST_SORT_VALUES = ['checklist_template_name', 'created_at'] as const;
export const MONTHLY_CHECKLIST_LIST_SORT_VALUES = ['contract_code', 'progress_rate', 'updated_at'] as const;

/** API-CHKL-01（02 2.3.2。雛形は削除しないため deleted を使わない） */
export const ChecklistTemplateListQuerySchema = buildListQuerySchema(CHECKLIST_TEMPLATE_LIST_SORT_VALUES, 'created_at')
  .omit({ deleted: true })
  .extend({ checklist_template_type: zEnum(CHECKLIST_TEMPLATE_TYPE_VALUES).optional() });

/** API-CHKL-06（02 2.3.2。year_month は必須） */
export const MonthlyChecklistListQuerySchema = buildListQuerySchema(MONTHLY_CHECKLIST_LIST_SORT_VALUES, 'contract_code')
  .omit({ deleted: true })
  .extend({
    year_month: zYearMonth,
    monthly_checklist_status: zEnum(MONTHLY_CHECKLIST_STATUS_VALUES).optional(),
    contract_status: zEnum(CONTRACT_STATUS_VALUES).optional(),
    /** 274：種別の絞り込み（旧の「全種別／仕入先／販売先」）。その種別のタスクを含む月次チェックリスト */
    checklist_template_type: zEnum(CHECKLIST_TEMPLATE_TYPE_VALUES).optional(),
  });

/** API-CHKL-13 の期間の上限（274：旧のチェックリスト詳細の 12 か月の表） */
export const MAX_CONTRACT_CHECKLIST_MONTHS = 12;

/** API-CHKL-13 のクエリの項目（OpenAPI の parameters 用。期間の検査は ContractChecklistsQuerySchema） */
export const ContractChecklistsQueryObjectSchema = z
  .object({ from_year_month: zYearMonth, to_year_month: zYearMonth })
  .strict();

/** API-CHKL-13 契約の月次チェックリスト（期間。from ≦ to・12 か月まで） */
export const ContractChecklistsQuerySchema = ContractChecklistsQueryObjectSchema.refine(
  (value) => {
    const months = (text: string) => Number(text.slice(0, 4)) * 12 + Number(text.slice(5, 7));
    const span = months(value.to_year_month) - months(value.from_year_month);
    return span >= 0 && span < MAX_CONTRACT_CHECKLIST_MONTHS;
  },
  { message: 'MSG-V-020', path: ['to_year_month'] },
);

export type ChecklistTemplateCreateInput = z.infer<typeof ChecklistTemplateCreateSchema>;
export type ChecklistTemplateUpdateInput = z.infer<typeof ChecklistTemplateUpdateSchema>;
export type ChecklistTemplateItem = z.infer<typeof ChecklistTemplateItemSchema>;
export type ChecklistTemplate = z.infer<typeof ChecklistTemplateSchema>;
export type ChecklistTemplateDefaultsResponse = z.infer<typeof ChecklistTemplateDefaultsResponseSchema>;
export type MonthlyChecklistTask = z.infer<typeof MonthlyChecklistTaskSchema>;
export type MonthlyChecklist = z.infer<typeof MonthlyChecklistSchema>;
export type MonthlyChecklistTaskUpdateInput = z.infer<typeof MonthlyChecklistTaskUpdateSchema>;
export type MonthlyChecklistTaskUpdateResponse = z.infer<typeof MonthlyChecklistTaskUpdateResponseSchema>;
export type DueAlertQuery = z.infer<typeof DueAlertQuerySchema>;
export type DueAlertTask = z.infer<typeof DueAlertTaskSchema>;
export type ChecklistGenerationInput = z.infer<typeof ChecklistGenerationSchema>;
export type ChecklistGenerationResponse = z.infer<typeof ChecklistGenerationResponseSchema>;
export type ChecklistSummary = z.infer<typeof ChecklistSummarySchema>;
export type ChecklistMatrix = z.infer<typeof ChecklistMatrixSchema>;
export type ChecklistMatrixQuery = z.infer<typeof ChecklistMatrixQuerySchema>;
export type ChecklistTemplateListQuery = z.infer<typeof ChecklistTemplateListQuerySchema>;
export type MonthlyChecklistListQuery = z.infer<typeof MonthlyChecklistListQuerySchema>;
export type ContractChecklistsQuery = z.infer<typeof ContractChecklistsQuerySchema>;
